import type { Region, Searcher, SearcherByKey, SearcherIndexed } from '../types';
import { dummyIndex, getRegionGlobal, getSearcherAuto, searchersNames, searcherUndefined } from './consts';
import { useAsyncTopDialog } from '@/components/dialog/dialog/composables/utils';

/**
 * Генерация Map ПС с Map регионов из массива searchers
 * - ключ для ПС - searcherKey
 * - ключ для региона - regionIndex
 *
 * Если поисковиков в списке нет, в список будет добавлен searcherUndefined
 *
 * Если регионов в списке нет, в список будет добавлен regionUndefined
 *
 * @param forFrequency - получить массив для просмотра частоты (или ставок)
 * @param autoRegion - добавить элемент с ключем -2, который в API заменяется на нужный ключ в зависимости от контекста
 * @param searchers - поисковики с регионами проекта, см.: get/projects_2/projects, show_searchers_and_regions = 1
 */
export const genSearcherByKey = (
	forFrequency: boolean = false,
	autoRegion: boolean = false,
	searchers: Searcher[] = [],
) => {
	let searcherByKey: SearcherByKey;

	if (forFrequency) {
		searcherByKey = genSearchersMapVolume(searchers);
	} else {
		searcherByKey = genSearchersMapCommon(searchers);
	}

	if (autoRegion) searcherByKey.set(dummyIndex, getSearcherAuto());
	if (!searcherByKey.size) searcherByKey.set(dummyIndex, searcherUndefined);

	return searcherByKey;
};

/**
 * Список ПС с регионами
 *
 * @param searchers
 * @param onlyEnabledRegions
 * @param forceSearchersKeys - ключи ПС, которые нееобходимо вывести в любом случае
 * @param forFrequency - для частоты будут выводиться только Яндекс, Google и Mail
 */
const genSearchersMapCommon = (
	searchers: Searcher[],
	onlyEnabledRegions: boolean = true,
	forceSearchersKeys: Searcher['key'][] = [],
	forFrequency: boolean = false,
) => {
	const searcherByKey: Map<Searcher['key'], SearcherIndexed> = new Map();

	// настроенные ПС
	searchers.forEach((searcher) => {
		if (!searcher.enabled) return;
		if (forFrequency && typeof searcher.key === 'number' && searcher.key > 1) return;

		const searcherI = { ...searcher } as SearcherIndexed;
		searcherI.regionByIndex = new Map();

		if (searcher.regions) {
			searcher.regions.forEach((region: Region) => {
				if (onlyEnabledRegions && !region.enabled) return;

				const regionI = { ...region };
				searcherI.regionByIndex.set(regionI.index, regionI);
			});
		}

		if (!searcherI.regionByIndex.size && !forceSearchersKeys.length) {
			// searcherI.regionByIndex.set(regionUndefined.index, regionUndefined);
		}

		if (
			// режим вывода ПС без регионов
			!searcher.regions ||

			// есть включенные регионы
			searcherI.regionByIndex.size ||

			// запрошен вывод конкретных ПС
			forceSearchersKeys.length
		) {
			searcherByKey.set(searcherI.key, searcherI);
		}
	});

	// дополнительные ПС
	forceSearchersKeys.forEach((searcherKey) => {
		if (searcherByKey.has(searcherKey)) return;

		const searcherI: SearcherIndexed = {
			key: searcherKey,
			name: searchersNames[searcherKey],
			regions: [],
			regionByIndex: new Map(),
		};

		searcherByKey.set(searcherI.key, searcherI);
	});

	return searcherByKey;
};

/**
 * Список ПС с регионами для проверки частоты (Яндекс, Google, Mail если добавлен в проект)
 *
 * @param searchers
 */
const genSearchersMapVolume = (searchers: Searcher[]) => {
	const serarcherByKey = genSearchersMapCommon(searchers, false, [0, 1], true);

	// в Mail нет регионов
	if (serarcherByKey.has(2)) {
		const searcherMail = serarcherByKey.get(2);
		if (searcherMail) searcherMail.regionByIndex = new Map();
	}

	// добавление глобального региона
	serarcherByKey.forEach((searcher) => {
		if (!searcher.regionByIndex) return;

		const region = { ...getRegionGlobal() };
		searcher.regionByIndex.set(region.index, region);
	});

	return serarcherByKey;
};

/**
 * Поиск региона по заданному критерию из списка поисковиков проекта
 *
 * @param forFrequency - поиск региона для просмотра частоты или ставок
 * @param searchRegion - объект с параметрами региона, который следует найти
 * @param searchers
 */
export const findRegion = (forFrequency: boolean, searchRegion: Partial<Region>, searchers: Searcher[] = []) => {
	const searcherByKey = genSearcherByKey(forFrequency, false, searchers);

	let findedRegion: Region | undefined;

	// поиск ПС
	searcherByKey.forEach((searcher) => {
		if (searchRegion.searcher_key !== undefined && searchRegion.searcher_key != searcher.key) return;
		if (!searcher.regions) return;

		// поиск региона
		searcher.regions.forEach((region) => {
			if (findedRegion) return;

			if (searchRegion.key !== undefined && searchRegion.key != region.key) return;
			if (searchRegion.index !== undefined && searchRegion.index != region.index) return;

			if (!forFrequency) {
				if (searchRegion.lang !== undefined && searchRegion.lang != region.lang) return;
				if (searchRegion.device !== undefined && searchRegion.device != region.device) return;
			}

			region.searcher_key = searcher.key;
			findedRegion = region;

			return false;
		});

		if (findedRegion) return false;
	});

	return findedRegion;
};

/**
 * Открыть диалоговое окно с выбором нескольких регионов
 *
 * @see import('../dialog_regionSelectorRegions/types').Props
 */
export const dialogRegionSelector = useAsyncTopDialog(() => {
	return import('@/components/project/regionSelector/dialog_regionSelectorRegions/dialog_regionSelectorRegions.vue');
});
